// src/components/JobDetailView.tsx
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonIcon,
} from '@ionic/react';
import { documentTextOutline, eyeOutline, personOutline, sparklesOutline } from 'ionicons/icons';
import React, { useEffect, useMemo, useState } from 'react';
import CoverLetterModal from './CoverLetterModal.tsx';
import FitAnalysisModal from './FitAnalysisModal.tsx';

// Professional loading component for job details
const ProfessionalJobDetailLoader = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: '200px'
  }}>
    <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            width: '3px',
            height: '24px',
            background: 'linear-gradient(to bottom, #4A6FA5, #2D4A6B)',
            borderRadius: '2px',
            animation: 'professionalLoading 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.1}s`
          }}
        />
      ))}
    </div>
    <p style={{ color: 'var(--app-text-color-secondary)', fontSize: '0.9rem', margin: 0 }}>
      Loading job details...
    </p>
  </div>
);

// Smart markdown-like formatter for job descriptions
const FormattedJobDescription: React.FC<{ description: string }> = ({ description }) => {
  const formatText = (text: string) => {
    // Split into paragraphs
    const paragraphs = text.split(/\n\s*\n/);

    return paragraphs.map((paragraph, index) => {
      const trimmed = paragraph.trim();
      if (!trimmed) return null;

      // Check for headers (** text ** or lines that end with colon and are short)
      if (trimmed.match(/^\*\*.*\*\*$/) || (trimmed.endsWith(':') && trimmed.length < 60 && !trimmed.includes('+'))) {
        return (
          <h4 key={index} style={{
            fontWeight: 700,
            fontSize: '1.1rem',
            color: '#667eea',
            margin: '20px 0 12px 0',
            borderBottom: '2px solid #764ba2',
            paddingBottom: '4px'
          }}>
            {trimmed.replace(/\*\*/g, '').replace(/:$/, '')}
          </h4>
        );
      }

      // Check for bullet points (lines starting with * or +)
      if (trimmed.match(/^[\*\+]\s/)) {
        const bulletLines = trimmed.split('\n').filter(line => line.trim());
        return (
          <ul key={index} style={{
            margin: '12px 0',
            paddingLeft: '20px',
            listStyleType: 'disc'
          }}>
            {bulletLines.map((line, i) => (
              <li key={i} style={{
                margin: '8px 0',
                lineHeight: '1.5',
                color: 'var(--app-text-color)'
              }}>
                {formatInlineText(line.replace(/^[\*\+]\s/, ''))}
              </li>
            ))}
          </ul>
        );
      }

      // Check for numbered lists
      if (trimmed.match(/^\d+\.\s/)) {
        const numberedLines = trimmed.split('\n').filter(line => line.trim());
        return (
          <ol key={index} style={{
            margin: '12px 0',
            paddingLeft: '20px'
          }}>
            {numberedLines.map((line, i) => (
              <li key={i} style={{
                margin: '8px 0',
                lineHeight: '1.5',
                color: 'var(--app-text-color)'
              }}>
                {formatInlineText(line.replace(/^\d+\.\s/, ''))}
              </li>
            ))}
          </ol>
        );
      }

      // Regular paragraph
      return (
        <p key={index} style={{
          margin: '12px 0',
          lineHeight: '1.6',
          color: 'var(--app-text-color)',
          textAlign: 'justify'
        }}>
          {formatInlineText(trimmed)}
        </p>
      );
    }).filter(Boolean);
  };

  const formatInlineText = (text: string) => {
    // Handle bold text (**text** or __text__)
    const parts = text.split(/(\*\*.*?\*\*|__.*?__)/g);
    return parts.map((part, i) => {
      if (part.match(/^\*\*.*\*\*$/) || part.match(/^__.*__$/)) {
        return (
          <strong key={i} style={{ color: '#667eea' }}>
            {part.replace(/[\*_]/g, '')}
          </strong>
        );
      }
      return part;
    });
  };

  return <div>{formatText(description)}</div>;
};

// --- Data Interfaces ---
interface JobDetailData {
  description?: string;
  requirements?: string[];
  original_url?: string;
  job_url?: string; // For scraped jobs
  site?: string; // For scraped jobs
  network_contacts?: {
    id: string;
    name: string;
    title: string;
  }[];
  // Additional fields for scraped jobs
  company?: string;
  location?: string;
  salary_min?: number;
  salary_max?: number;
  job_type?: string;
  is_remote?: boolean;
  currency?: string;
}

interface JobDetailViewProps {
  jobId: string | null;
  jobsCache?: Job[]; // Pass cached jobs to avoid re-fetching
  activeSearchId?: string; // Scope fallback fetch to currently active search
}

// Import Job interface
interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  salary_min?: number;
  salary_max?: number;
  date_posted: string;
  job_type?: string;
  contacts_count?: number;
  match_percentage: number;
  job_url?: string;
  site?: string;
  is_remote?: boolean;
  description?: string;
  currency?: string;
  salary_source?: string;
}

const JobDetailView: React.FC<JobDetailViewProps> = ({ jobId, jobsCache, activeSearchId }) => {
  const [details, setDetails] = useState<JobDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showFitAnalysisModal, setShowFitAnalysisModal] = useState(false);
  const [showCoverLetterModal, setShowCoverLetterModal] = useState(false);

  // Add CSS animations for professional loading
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes professionalLoading {
        0%, 40%, 100% { transform: scaleY(0.4); opacity: 0.5; }
        20% { transform: scaleY(1.0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // PERFORMANCE OPTIMIZATION: Memoize job lookup to prevent re-computation
  const cachedJob = useMemo(() => {
    if (!jobId || !jobsCache) return null;
    return jobsCache.find(job => job.id === jobId) || null;
  }, [jobId, jobsCache]);

  // Single source of truth: jobsCache
  // No API calls - job search results already contain full details
  useEffect(() => {
    if (!jobId) {
      setDetails(null);
      setError(null);
      return;
    }

    // Find job in cache (should always exist since we loaded search results)
    const jobInCache = jobsCache?.find(job => job.id === jobId);

    if (jobInCache) {
      console.log('✅ Using job from cache (no API call):', jobId);
      setDetails({
        description: jobInCache.description || 'No description available.',
        requirements: [],
        job_url: jobInCache.job_url,
        original_url: jobInCache.job_url,
        site: jobInCache.site,
        company: jobInCache.company,
        location: jobInCache.location,
        salary_min: jobInCache.salary_min,
        salary_max: jobInCache.salary_max,
        job_type: jobInCache.job_type,
        is_remote: jobInCache.is_remote,
        currency: jobInCache.currency,
        network_contacts: [],
      });
      setError(null);
    } else if (jobsCache && jobsCache.length > 0) {
      // Job not found in loaded cache - this shouldn't happen
      console.warn('⚠️ Job not found in cache:', jobId);
      setError('Please select a job from the list on the left to view its details.');
      setDetails(null);
    }
    // Else: cache still loading, wait for it
  }, [jobId, jobsCache]);

  if (!jobId) {
    return (
      <div style={{ padding: '20px', height: '100%' }}>
        <IonCard style={{ '--background': '#ffffff', border: '1px solid #e9ecef' }}>
          <IonCardContent style={{ padding: '24px', textAlign: 'center' }}>
            <div style={{
              background: '#f8f9fa',
              borderRadius: '50%',
              width: '64px',
              height: '64px',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <IonIcon icon={eyeOutline} style={{ fontSize: '32px', color: '#6c757d' }} />
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#212529' }}>
              No Job Selected
            </h3>
            <p style={{ margin: 0, color: '#6c757d', fontSize: '14px', lineHeight: '1.5' }}>
              Select a job from the list to view detailed information, requirements, and company details.
            </p>
          </IonCardContent>
        </IonCard>
      </div>
    );
  }

  // Show message if job not found in cache
  if (error) {
    return (
      <div style={{ padding: '20px', height: '100%' }}>
        <IonCard style={{ '--background': '#f8f9fa', border: '1px solid #e9ecef' }}>
          <IonCardContent style={{ padding: '24px', textAlign: 'center' }}>
            <div style={{
              background: '#e3f2fd',
              borderRadius: '50%',
              width: '64px',
              height: '64px',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <IonIcon icon={eyeOutline} style={{ fontSize: '32px', color: '#1976d2' }} />
            </div>
            <p style={{ margin: 0, color: '#495057', fontSize: '14px', lineHeight: '1.5' }}>
              {error}
            </p>
          </IonCardContent>
        </IonCard>
      </div>
    );
  }

  if (!details) {
    // If no details but no error and not loading, show loading state (might be transitioning between searches)
    return <ProfessionalJobDetailLoader />;
  }

  // TypeScript guard: ensure details exists before rendering
  if (!details) {
    return <ProfessionalJobDetailLoader />;
  }

  return (
    <div>
      {/* --- Job Details Card --- */}
      <IonCard style={{'--background': 'var(--app-card-background-color)'}}>
        <IonCardContent>
          <h3 style={{fontWeight: 600, color: 'var(--app-text-color)'}}>Job Details</h3>

          {/* Am I a Fit? Button */}
          <IonButton
            expand="block"
            onClick={() => setShowFitAnalysisModal(true)}
            style={{
              marginBottom: '12px',
              '--background': '#1e3a8a',
              '--background-hover': '#1e40af',
              '--background-activated': '#1e3a8a',
              '--color': 'white',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            <IonIcon icon={sparklesOutline} slot="start" />
            Am I a Fit?
          </IonButton>

          {/* Generate Cover Letter Button */}
          <IonButton
            expand="block"
            onClick={() => setShowCoverLetterModal(true)}
            style={{
              marginBottom: '12px',
              '--background': '#10b981',
              '--background-hover': '#059669',
              '--background-activated': '#10b981',
              '--color': 'white',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            <IonIcon icon={documentTextOutline} slot="start" />
            Generate Cover Letter
          </IonButton>

          <IonButton
            fill="outline"
            expand="block"
            href={details.original_url || details.job_url}
            target="_blank"
            style={{marginBottom: '20px'}}
            disabled={!details.original_url && !details.job_url}
          >
            <IonIcon icon={eyeOutline} slot="start" />
            View Original Posting
          </IonButton>

          {/* Additional info for scraped jobs */}
          {details.site && (
            <div style={{marginBottom: '16px', padding: '12px', backgroundColor: 'var(--ion-color-light)', borderRadius: '8px'}}>
              <p style={{margin: 0, fontSize: '0.9rem', color: 'var(--app-text-color-secondary)'}}>
                <strong>Source:</strong> {details.site}
                {details.is_remote && <span> • Remote Work Available</span>}
                {details.job_type && <span> • {details.job_type}</span>}
              </p>
              {(details.salary_min || details.salary_max) && (
                <p style={{margin: '4px 0 0', fontSize: '0.9rem', color: '#667eea'}}>
                  <strong>Salary:</strong> {details.salary_min && details.salary_max
                    ? `${details.currency || '$'}${Math.round(details.salary_min / 1000)}k - ${Math.round(details.salary_max / 1000)}k`
                    : details.salary_min
                      ? `${details.currency || '$'}${Math.round(details.salary_min / 1000)}k+`
                      : details.salary_max
                        ? `Up to ${details.currency || '$'}${Math.round(details.salary_max / 1000)}k`
                        : 'Not specified'
                  }
                </p>
              )}
            </div>
          )}

          <h4 style={{fontWeight: 600, marginTop: '16px'}}>Description</h4>
          <div style={{lineHeight: '1.6'}}>
            {details.description ? (
              <FormattedJobDescription description={details.description} />
            ) : (
              <p>No description available.</p>
            )}
          </div>

          {details.requirements && details.requirements.length > 0 && (
            <>
              <h4 style={{fontWeight: 600, marginTop: '16px'}}>Requirements</h4>
              <ul style={{paddingLeft: '20px', margin: 0}}>
                {details.requirements.map((req, i) => <li key={i}>{req}</li>)}
              </ul>
            </>
          )}
        </IonCardContent>
      </IonCard>

      {/* --- Network Card --- */}
      <IonCard style={{'--background': 'var(--app-card-background-color)', marginTop: '20px'}}>
        <IonCardContent>
          <h3 style={{fontWeight: 600, color: 'var(--app-text-color)'}}>Your Network</h3>
          {details.network_contacts && details.network_contacts.length > 0 ? (
            details.network_contacts.map(contact => (
              <div key={contact.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px'}}>
                <div>
                  <p style={{fontWeight: 500, margin: 0}}>{contact.name}</p>
                  <p style={{fontSize: '0.9rem', color: 'var(--app-text-color-secondary)', margin: '4px 0 0'}}>{contact.title}</p>
                </div>
                <IonButton size="small">
                  <IonIcon icon={personOutline} slot="start" />
                  Generate Email
                </IonButton>
              </div>
            ))
          ) : (
            <p>{details.site ? `No contacts found at ${details.company || 'this company'}. Try expanding your network or searching for employees.` : 'No contacts found at this company.'}</p>
          )}
        </IonCardContent>
      </IonCard>

      {/* Fit Analysis Modal */}
      <FitAnalysisModal
        isOpen={showFitAnalysisModal}
        onClose={() => setShowFitAnalysisModal(false)}
        jobId={jobId}
        jobTitle={cachedJob?.title}
        userSearchId={activeSearchId}
        isScrapedJob={!!cachedJob?.site}
      />

      {/* Cover Letter Modal */}
      {showCoverLetterModal && (
        <CoverLetterModal
          isOpen={showCoverLetterModal}
          onClose={() => setShowCoverLetterModal(false)}
          jobId={jobId}
          jobTitle={cachedJob?.title}
          companyName={cachedJob?.company}
          userSearchId={activeSearchId}
          isScrapedJob={!!cachedJob?.site}
        />
      )}
    </div>
  );
};

export default JobDetailView;